import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'
import {
  Add, Receipt, Payment, Assessment, Delete, AutoAwesome,
} from '@mui/icons-material'
import {
  useRateCards, useCreateRateCard, useDeleteRateCard, RateCard,
  useCustomerInvoices, useMarkInvoicePaid, useGenerateInvoice,
  useVehiclePnL, useCustomers,
} from '../../hooks/useWave2'

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default', sent: 'info', paid: 'success', void: 'warning',
}

const PRICING_LABEL: Record<string, string> = {
  per_km: 'per km', per_stop: 'per stop', per_hour: 'per hour',
  per_delivery: 'per delivery', flat: 'flat',
}

export default function InvoicingPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, color: '#0f172a' }}>
        {t('invoicing.title', 'Invoicing & Revenue')}
      </Typography>
      <Typography variant="caption" color="#64748b" sx={{ mb: 3, display: 'block' }}>
        {t('invoicing.subtitle', 'Bill your customers, track collections, see revenue per vehicle.')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('invoicing.tab_invoices', 'Invoices')} icon={<Receipt fontSize="small" />} iconPosition="start" />
        <Tab label={t('invoicing.tab_rate_cards', 'Rate Cards')} icon={<Payment fontSize="small" />} iconPosition="start" />
        <Tab label={t('invoicing.tab_pnl', 'Revenue per Vehicle')} icon={<Assessment fontSize="small" />} iconPosition="start" />
      </Tabs>

      {tab === 0 && <InvoicesTab />}
      {tab === 1 && <RateCardsTab />}
      {tab === 2 && <PnLTab />}
    </Box>
  )
}

function InvoicesTab() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<string>('')
  const invoices = useCustomerInvoices(statusFilter || undefined)
  const customers = useCustomers()
  const markPaid = useMarkInvoicePaid()
  const generate = useGenerateInvoice()
  const [genCust, setGenCust] = useState('')

  return (
    <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          label={t('invoicing.filter_status', 'Status')} sx={{ minWidth: 140 }}
        >
          <MenuItem value="">{t('common.all', 'All')}</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="void">Void</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <TextField
          select size="small" value={genCust} onChange={(e) => setGenCust(e.target.value)}
          label={t('invoicing.gen_for', 'Generate for…')} sx={{ minWidth: 200 }}
        >
          {(customers.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined" startIcon={<AutoAwesome />}
          disabled={!genCust || generate.isPending}
          onClick={() => generate.mutate(genCust)}
        >
          {t('invoicing.generate', 'Generate')}
        </Button>
      </Stack>

      {invoices.isLoading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : (invoices.data?.length ?? 0) === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
          {t('invoicing.no_invoices', 'No invoices yet.')}
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('invoicing.number', 'Number')}</TableCell>
              <TableCell>{t('invoicing.amount', 'Amount')}</TableCell>
              <TableCell>{t('invoicing.status', 'Status')}</TableCell>
              <TableCell>{t('invoicing.due', 'Due')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(invoices.data ?? []).map((inv) => (
              <TableRow key={inv.id} hover>
                <TableCell><Typography fontFamily="monospace" fontSize={13}>{inv.number}</Typography></TableCell>
                <TableCell>{inv.amount.toFixed(2)} {inv.currency}</TableCell>
                <TableCell><Chip size="small" label={inv.status} color={STATUS_COLOR[inv.status] ?? 'default'} /></TableCell>
                <TableCell>{inv.due_at ? new Date(inv.due_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="right">
                  {inv.status !== 'paid' && (
                    <Button size="small" onClick={() => markPaid.mutate(inv.id)} disabled={markPaid.isPending}>
                      {t('invoicing.mark_paid', 'Mark paid')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  )
}

function RateCardsTab() {
  const { t } = useTranslation()
  const cards = useRateCards()
  const customers = useCustomers()
  const create = useCreateRateCard()
  const remove = useDeleteRateCard()
  const [form, setForm] = useState<Partial<RateCard>>({ pricing_model: 'per_km', currency: 'USD', is_active: true })
  const [open, setOpen] = useState(false)

  return (
    <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography fontWeight={700}>{t('invoicing.rate_cards', 'Rate Cards')}</Typography>
        <Button startIcon={<Add />} onClick={() => setOpen(true)}>{t('invoicing.new_card', 'New rate card')}</Button>
      </Stack>
      {cards.isLoading ? (
        <CircularProgress size={20} />
      ) : (cards.data?.length ?? 0) === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
          {t('invoicing.no_cards', 'No rate cards yet.')}
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name', 'Name')}</TableCell>
              <TableCell>{t('invoicing.customer', 'Customer')}</TableCell>
              <TableCell>{t('invoicing.pricing', 'Pricing')}</TableCell>
              <TableCell>{t('invoicing.rate', 'Rate')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(cards.data ?? []).map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>{customers.data?.find((cu) => cu.id === c.customer_id)?.name ?? '—'}</TableCell>
                <TableCell>{PRICING_LABEL[c.pricing_model]}</TableCell>
                <TableCell>{c.unit_rate.toFixed(2)} {c.currency}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => remove.mutate(c.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.new_card', 'New rate card')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('common.name', 'Name')} value={form.name ?? ''}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
            <TextField select label={t('invoicing.customer', 'Customer')} value={form.customer_id ?? ''}
                       onChange={(e) => setForm({ ...form, customer_id: e.target.value || null })} fullWidth>
              <MenuItem value="">{t('invoicing.global_card', 'Global (any customer)')}</MenuItem>
              {(customers.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label={t('invoicing.pricing_model', 'Pricing model')}
                       value={form.pricing_model ?? 'per_km'}
                       onChange={(e) => setForm({ ...form, pricing_model: e.target.value as RateCard['pricing_model'] })} fullWidth>
              {(['per_km', 'per_stop', 'per_hour', 'per_delivery', 'flat'] as const).map((m) => (
                <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1}>
              <TextField label={t('invoicing.unit_rate', 'Unit rate')} type="number" value={form.unit_rate ?? 0}
                         onChange={(e) => setForm({ ...form, unit_rate: Number(e.target.value) })} fullWidth />
              <TextField label={t('common.currency', 'Currency')} value={form.currency ?? 'USD'}
                         onChange={(e) => setForm({ ...form, currency: e.target.value })} sx={{ width: 100 }} />
            </Stack>
            <TextField label={t('invoicing.minimum_charge', 'Minimum charge')} type="number" value={form.minimum_charge ?? 0}
                       onChange={(e) => setForm({ ...form, minimum_charge: Number(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" disabled={!form.name}
                  onClick={async () => { await create.mutateAsync(form); setOpen(false); setForm({ pricing_model: 'per_km', currency: 'USD', is_active: true }) }}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

function PnLTab() {
  const { t } = useTranslation()
  const pnl = useVehiclePnL()

  if (pnl.isLoading) return <CircularProgress size={28} />
  if (!pnl.data || pnl.data.length === 0) {
    return (
      <Card sx={{ p: 4, textAlign: 'center', color: '#64748b', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
        {t('invoicing.no_pnl', 'No P&L data yet — generate invoices first.')}
      </Card>
    )
  }

  return (
    <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('invoicing.period', 'Period')}</TableCell>
            <TableCell>{t('invoicing.vehicle', 'Vehicle')}</TableCell>
            <TableCell align="right">{t('invoicing.revenue', 'Revenue')}</TableCell>
            <TableCell align="right">{t('invoicing.fuel', 'Fuel')}</TableCell>
            <TableCell align="right">{t('invoicing.maint', 'Maintenance')}</TableCell>
            <TableCell align="right">{t('invoicing.driver_cost', 'Driver')}</TableCell>
            <TableCell align="right">{t('invoicing.cm', 'Margin')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pnl.data.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>{new Date(p.period).toLocaleDateString()}</TableCell>
              <TableCell><Typography fontFamily="monospace" fontSize={12}>{p.device_id.slice(0, 8)}</Typography></TableCell>
              <TableCell align="right">{p.revenue.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ color: '#ef4444' }}>{p.fuel_cost.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ color: '#ef4444' }}>{p.maintenance_cost.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ color: '#ef4444' }}>{p.driver_cost.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ color: p.contribution_margin >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                {p.contribution_margin.toFixed(2)} {p.currency}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}
